import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../../app/hooks';
import { getDateRange } from '../../../../utils/fix-uct-time';
import { IToast } from '../../../../interfaces/components/IToast';
import { tryToFetchAllTeamMembers } from '../../../../store/brokers/partners/teams/teamsSlice';
import { tryToFetchAllJourneys, tryToFetchActiveTrips, tryToGetFeedbackStats } from '../../../../store/admin/trips/adminTripsSlice';
import ManageTripsModal from '../modals/ManageTripsModal';
import TripFilters from '../components/TripFilters';
import Toast from '../../../../shared/toast';
import RebeccaTopicsDashboard from '../components/RebeccaTopicsDashboard';


const ExecutiveTab = () => {
    const isFirstRun = useRef(true);
    const dispatch = useAppDispatch();
    const [trainManagers, setTrainManagers] = useState<any>();
    const [staff, setStaff] = useState<any>();
    const [trips, setTrips] = useState<any>();
    const [showToast, setShowToast] = useState<IToast | null>();
    const [selectedTrip, setSelectedTrip] = useState<any>();
    const [selectedStaff, setSelectedStaff] = useState<any>();
    const [selectedStaffRole, setSelectedStaffRole] = useState<any>();
    const [selectedTrainManager, setSelectedTrainManager] = useState<any>();
    const [selectedDateRange, setSelectedDateRange] = useState<any>();
    const [showNewTripModal, setShowNewTripModal] = useState<{ show: boolean, data?: any }>();

    const onGettingData = async () => {
        try {
            const trainManagerRes = await dispatch(tryToFetchAllTeamMembers({ role: 'train_manager' })).unwrap();
            const formatTrainManagers = trainManagerRes && trainManagerRes?.length > 0 && trainManagerRes?.map((item: any) => {
                return {
                    ...item || {},
                    label: item?.name || item?.username,
                    value: item?._id,
                }
            })
            setTrainManagers(formatTrainManagers)
            const journeysRes = await dispatch(tryToFetchAllJourneys()).unwrap();
            const formatJourneys = journeysRes?.elements && journeysRes?.elements?.length > 0 && journeysRes?.elements?.map((item: any) => {
                return {
                    ...item || {},
                    label: item?.journeyTitle,
                    value: item?._id,
                }
            })
            setTrips(formatJourneys || undefined)
            const staffRes = await dispatch(tryToFetchAllTeamMembers({ type: 'staff' })).unwrap();
            const formatStaff = staffRes && staffRes?.length > 0 && staffRes?.map((item: any) => {
                return {
                    ...item || {},
                    label: item?.name || item?.username,
                    value: item?._id,
                }
            })
            setStaff(formatStaff)
        } catch (err) {
            console.error(err)
        }
    }

    const onGettingStats = async () => {
        try {
            let settingsFilters: any = {}
            if (selectedTrip && selectedTrip?.value !== 'all') {
                settingsFilters = {
                    ...settingsFilters || {},
                    itinerary: selectedTrip?.value
                }
            }
            if (selectedDateRange) {
                const range = getDateRange([selectedDateRange?.[0], selectedDateRange?.[1]]);
                settingsFilters = {
                    ...settingsFilters || {},
                    startDate: range?.dateRangeMin,
                    endDate: range?.dateRangeMax
                }
            }
            if (selectedTrainManager && selectedTrainManager?.value !== 'all') {
                settingsFilters = {
                    ...settingsFilters || {},
                    trainManagerId: selectedTrainManager?.value
                }
            }
            if (selectedStaff && selectedStaff?.value !== 'all') {
                settingsFilters = {
                    ...settingsFilters || {},
                    crewMemberId: selectedStaff?.value
                }
            }
            if (selectedStaffRole && selectedStaffRole?.value !== 'all') {
                settingsFilters = {
                    ...settingsFilters || {},
                    crewMemberRole: selectedStaffRole?.value
                }
            }
            await dispatch(tryToGetFeedbackStats(settingsFilters)).unwrap();
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        onGettingData()
        onGettingStats()
    }, [])

    const onChangeDateRange = (value: any) => {
        setSelectedDateRange(value)
    }

    const onClickTrip = (type: string) => {
        if (type === 'new') {
            setShowNewTripModal({
                show: true,
            })
        }
    }

    const onChangeFilters = (value: any, type?: string) => {
        if (type === 'journey') setSelectedTrip(value);
        if (type === 'trainManager') setSelectedTrainManager(value);
        if (type === 'staff') setSelectedStaff(value);
        if (type === 'staffRole') setSelectedStaffRole(value);
        setSelectedDateRange(undefined)
    };

    const onCloseTripModal = () => {
        setShowNewTripModal(undefined)
    }

    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }

        onGettingStats();
    }, [
        selectedDateRange,
        selectedStaff,
        selectedStaffRole,
        selectedTrainManager,
        selectedTrip
    ]);

    const onClickClearAll = () => {
        setSelectedDateRange(undefined)
        setSelectedStaff(undefined)
        setSelectedTrainManager(undefined)
        setSelectedTrip(undefined)
        setSelectedStaffRole(undefined)
    }

    const onSaveSuccessfulTrip = (message: string) => {
        setShowToast({ type: 'success', message: message });
        onGettingStats();
        dispatch(tryToFetchActiveTrips({ pageNumber: 1, pageSize: 10, data: { status: 'active' } }));
        onCloseTripModal()
    }

    // Build exec-by-category filters from current filter state
    const execFilters = React.useMemo(() => {
        const f: Record<string, any> = {};
        if (selectedTrip && selectedTrip?.value !== 'all') {
            f.itinerary = selectedTrip.value;
        }
        if (selectedDateRange) {
            const range = getDateRange([selectedDateRange?.[0], selectedDateRange?.[1]]);
            if (range?.dateRangeMin) f.startDate = range.dateRangeMin;
            if (range?.dateRangeMax) f.endDate = range.dateRangeMax;
        }
        if (selectedTrainManager && selectedTrainManager?.value !== 'all') {
            f.trainManagerId = selectedTrainManager.value;
        }
        if (selectedStaff && selectedStaff?.value !== 'all') {
            f.crewMemberId = selectedStaff.value;
        }
        if (selectedStaffRole && selectedStaffRole?.value !== 'all') {
            f.crewMemberRole = selectedStaffRole.value;
        }
        return f;
    }, [selectedTrip, selectedDateRange, selectedTrainManager, selectedStaff, selectedStaffRole]);

    return (
        <div>
            <TripFilters
                allTrips={trips}
                trainManagers={trainManagers}
                staff={staff}
                selectedDateRange={selectedDateRange}
                selectedStaff={selectedStaff}
                selectedStaffRole={selectedStaffRole}
                selectedTrainManager={selectedTrainManager}
                selectedTrip={selectedTrip}
                onChangeDateRange={onChangeDateRange}
                onChangeFilters={onChangeFilters}
                onClickTrip={onClickTrip}
                onClickClearAll={onClickClearAll}
            />
            {/* <div className='my-5'>
                <p className='font-bold dark:text-primary-darkMode ml-2'>Overview</p>
            </div> */}
            <RebeccaTopicsDashboard trainManagers={trainManagers} staff={staff} execFilters={execFilters} />
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* <div className='flex flex-col h-full'>
                    <div className='border border-1 border-[#6366F1] rounded-lg px-4 py-4 mb-2 flex-1'>
                        <p className='font-bold dark:text-primary-darkMode mb-3'>Total feedback</p>
                        <div className='grid grid-cols-2 gap-4'>
                            <div>
                                <p className='text-2xl font-bold dark:text-primary-darkMode'>{stats?.totalByGuest || 0}</p>
                                <p className='dark:text-primary-darkMode mb-2 text-sm'>By Guests</p>
                            </div>
                            <div>
                                <p className='text-2xl font-bold dark:text-primary-darkMode'>{stats?.totalByStaff || 0}</p>
                                <p className='dark:text-primary-darkMode mb-2 text-sm'>By Staff</p>
                            </div>
                        </div>
                    </div>
                </div> */}
                {/* <div className='flex flex-col h-full'>
                    <div className='border border-1 border-[#6366F1] rounded-lg px-4 py-4 mb-2 flex-1'>
                        <p className='font-bold dark:text-primary-darkMode mb-3'>Sentiment of feedback</p>
                        <div className='grid grid-cols-3 gap-4'>
                            <div className='!text-[#10B981]'>
                                <p className='text-2xl font-bold'>{positive}</p>
                                <p className='mb-2 text-sm'>Positive</p>
                            </div>
                            <div className='!text-[#F59E0B]'>
                                <p className='text-2xl font-bold'>{neutral}</p>
                                <p className='mb-2 text-sm'>Neutral</p>
                            </div>
                            <div className='!text-[#EF4444]'>
                                <p className='text-2xl font-bold'>{negative}</p>
                                <p className='mb-2 text-sm'>Negative</p>
                            </div>
                        </div>
                    </div>
                </div> */}

            </div>
            {/* <div className='grid grid-cols-1 lg:grid-cols-8 gap-4 my-4'>

                    <div className='col-span-1 lg:col-span-4 !rounded-lg border p-3 relative'>
                        <p className='text-[#1C1C1C] dark:text-primary-darkMode font-semibold mb-5'>Status</p>
                        <div className={`flex flex-col space-x-2 my-3`}>
                            <div className='text-sm dark:text-primary-darkMode ml-4 truncate whitespace-nowrap overflow-hidden'>
                                Allocated & Acknowledged ({acknowledged})
                            </div>
                            <div className='flex items-center space-x-2 my-2'>
                                <div className='flex-1 bg-gray-200 h-2 rounded-full relative'>
                                    <div
                                        title={`Allocated & Acknowledged: ${acknowledged}`}
                                        className={`h-2 rounded-full absolute top-0 left-0 bg-black`}
                                        style={{ width: widthAcknowledged }}
                                    />
                                </div>
                                <p className='dark:text-primary-darkMode text-2xl ml-2'>{acknowledgedPercentage}%</p>
                            </div>
                        </div>
                        <div className={`flex flex-col space-x-2 my-3`}>
                            <div className='text-sm dark:text-primary-darkMode ml-4 truncate whitespace-nowrap overflow-hidden dark:text-primary-darkMode'>
                                Allocated but Not Acknowledged ({notacknowledged})
                            </div>
                            <div className='flex items-center space-x-2 my-2'>
                                <div className='flex-1 bg-gray-200 h-2 rounded-full relative'>
                                    <div
                                        title={`Allocated but Not Acknowledged: ${notacknowledged}`}
                                        className={`h-2 rounded-full absolute top-0 left-0 bg-black`}
                                        style={{ width: widthNotacknowledged }}
                                    />
                                </div>
                                <p className='dark:text-primary-darkMode text-2xl ml-2'>{notacknowledgedPercentage}%</p>
                            </div>
                        </div>
                        <div className={`flex flex-col space-x-2 my-3`}>
                            <div className='text-sm dark:text-primary-darkMode ml-4 truncate whitespace-nowrap overflow-hidden dark:text-primary-darkMode'>
                                Unallocated ({unallocated})
                            </div>
                            <div className='flex items-center space-x-2 my-2'>
                                <div className='flex-1 bg-gray-200 h-2 rounded-full relative'>
                                    <div
                                        title={`Unallocated: ${unallocatedPercentage}`}
                                        className={`h-2 rounded-full absolute top-0 left-0 bg-black`}
                                        style={{ width: widthUnallocated }}
                                    />
                                </div>
                                <p className='dark:text-primary-darkMode text-2xl ml-2'>{unallocatedPercentage}%</p>
                            </div>
                        </div>
                    </div>
                    <div className='col-span-1 lg:col-span-4 !rounded-lg border p-3 relative'>
                        <p className='text-[#1C1C1C] dark:text-primary-darkMode font-semibold mb-5'>Sentiment of Feedback</p>
                        <div className={`flex flex-col space-x-2 my-3`}>
                            <div className='text-sm dark:text-primary-darkMode ml-4 truncate whitespace-nowrap overflow-hidden dark:text-primary-darkMode'>
                                Positive
                            </div>
                            <div className='flex items-center space-x-2 my-2'>
                                <div className='flex-1 bg-gray-200 h-2 rounded-full relative'>
                                    <div
                                        title={`Positive: ${positive}`}
                                        className={`h-2 rounded-full absolute top-0 left-0 bg-[#10B981]`}
                                        style={{ width: widthPositive }}
                                    />
                                </div>
                                <p className='text-[#10B981] text-2xl ml-2'>{positivePercentage}%</p>
                            </div>
                        </div>
                        <div className={`flex flex-col space-x-2 my-3`}>
                            <div className='text-sm dark:text-primary-darkMode ml-4 truncate whitespace-nowrap overflow-hidden dark:text-primary-darkMode'>
                                Neutral
                            </div>
                            <div className='flex items-center space-x-2 my-2'>
                                <div className='flex-1 bg-gray-200 h-2 rounded-full relative'>
                                    <div
                                        title={`Neutral: ${neutral}`}
                                        className={`h-2 rounded-full absolute top-0 left-0 bg-[#F59E0B]`}
                                        style={{ width: widthNeutral }}
                                    />
                                </div>
                                <p className='text-[#F59E0B] text-2xl ml-2'>{neutralPercentage}%</p>
                            </div>
                        </div>
                        <div className={`flex flex-col space-x-2 my-3`}>
                            <div className='text-sm ml-4 truncate whitespace-nowrap overflow-hidden dark:text-primary-darkMode'>
                                Negative
                            </div>
                            <div className='flex items-center space-x-2 my-2'>
                                <div className='flex-1 bg-gray-200 h-2 rounded-full relative'>
                                    <div
                                        title={`Negative: ${negative}`}
                                        className={`h-2 rounded-full absolute top-0 left-0 bg-[#EF4444]`}
                                        style={{ width: widthNegative }}
                                    />
                                </div>
                                <p className='text-[#EF4444] text-2xl ml-2'>{negativePercentage}%</p>
                            </div>
                        </div>
                    </div>
                </div> */}
            {showNewTripModal?.show &&
                <ManageTripsModal
                    open={showNewTripModal?.show}
                    journeys={trips}
                    data={showNewTripModal?.data}
                    onSaveSuccessful={onSaveSuccessfulTrip}
                    onClose={onCloseTripModal}
                />
            }
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }
        </div>
    )
}

export default ExecutiveTab